import React, { useState } from 'react';
import CodeEditor from './CodeEditor';
import OutputPanel from './OutputPanel';
import QuizRenderer from './QuizRenderer';
import MascotMessage from './MascotMessage';
import { useCodeExecution } from '../hooks/useCodeExecution';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import './StepRenderer.css';

/**
 * StepRenderer Component
 * Renders any lesson step type dynamically
 */
function StepRenderer({
  step,
  language = 'python',
  onComplete,
  onAnswer
}) {
  const { executeForLanguage, isRunning } = useCodeExecution();
  const [executionResult, setExecutionResult] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [hintLevel, setHintLevel] = useState(0);
  const [challengeCompleted, setChallengeCompleted] = useState(false);
  const [hasRunCode, setHasRunCode] = useState(false);

  const handleRunCode = async (code) => {
    const result = await executeForLanguage(language, code);
    setExecutionResult({
      output: result.output || '',
      error: result.error,
      success: result.success,
      executionTime: result.executionTime
    });
    
    // Play sound/haptic based on result
    if (result.success && !result.error) {
      soundService.playSuccess();
      hapticService.success();
      setHasRunCode(true);
    } else if (result.error) {
      soundService.playError();
      hapticService.error();
    }
  };

  const handleChallengeComplete = () => {
    // Play success sound and haptic
    soundService.playAchievement();
    hapticService.notification();
    setChallengeCompleted(true);
    // Don't auto-advance - let user click "Next" button in navigation
    // The success message stays visible until user moves forward
  };

  const renderStepContent = () => {
    switch (step.type) {
      case 'story':
        return renderStory();
      case 'visual-explanation':
        return renderVisualExplanation();
      case 'interactive-demo':
        return renderInteractiveDemo();
      case 'concept-breakdown':
        return renderConceptBreakdown();
      case 'concept':
        return renderConcept();
      case 'explanation':
        return renderExplanation();
      case 'practice':
        return renderPractice();
      case 'quiz':
        return renderQuiz();
      case 'mini-challenge':
        return renderMiniChallenge();
      case 'challenge':
        return renderChallenge();
      case 'summary':
        return renderSummary();
      default:
        return renderGeneric();
    }
  };

  const renderStory = () => (
    <div className="step-story">
      {step.mascot && (
        <MascotMessage 
          message={step.mascot.message} 
          state={step.mascot.state || 'explain'}
        />
      )}
      
      <div className="story-content">
        <h2>{step.title}</h2>
        <p className="story-text">{step.content?.text}</p>
      </div>

      {step.visual && (
        <div className="story-visual">
          <img 
            src={`/assets/media/${step.visual.src}`} 
            alt={step.visual.alt} 
          />
        </div>
      )}
    </div>
  );

  const renderVisualExplanation = () => (
    <div className="step-visual-explanation">
      <h2>{step.title}</h2>
      
      {step.comparison && (
        <div className="comparison-grid">
          <div className="comparison-item left">
            <img src={`/assets/media/${step.comparison.left.image}`} alt="Left" />
            <p>{step.comparison.left.text}</p>
          </div>
          <div className="comparison-vs">VS</div>
          <div className="comparison-item right">
            <img src={`/assets/media/${step.comparison.right.image}`} alt="Right" />
            <p>{step.comparison.right.text}</p>
          </div>
        </div>
      )}

      {step.diagram && (
        <div className="diagram-container">
          <img src={`/assets/media/${step.diagram.src}`} alt={step.diagram.alt} />
        </div>
      )}

      {step.funFact && (
        <div className="fun-fact">
          <span className="fun-fact-icon">💡</span>
          <p>{step.funFact}</p>
        </div>
      )}
    </div>
  );

  const renderInteractiveDemo = () => (
    <div className="step-interactive-demo">
      <h2>{step.title}</h2>
      
      {step.content?.text && (
        <p className="demo-intro">{step.content.text}</p>
      )}
      
      {step.codeExample && (
        <div className="demo-code-section">
          <CodeEditor
            code={step.codeExample.starterCode}
            language={language}
            editable={step.codeExample.editable}
            runnable={step.codeExample.runnable}
            onRun={handleRunCode}
            running={isRunning}
          />
          
          {executionResult && (
            <OutputPanel
              output={executionResult.output}
              error={executionResult.error}
              success={executionResult.success}
              executionTime={executionResult.executionTime}
              visible={true}
            />
          )}
        </div>
      )}

      {step.mascot && (
        <MascotMessage 
          message={step.mascot.message} 
          state={step.mascot.state || 'happy'}
        />
      )}

      {step.explanation && (
        <div className="demo-explanation">
          <p>{step.explanation}</p>
        </div>
      )}
    </div>
  );

  const renderConceptBreakdown = () => (
    <div className="step-concept-breakdown">
      <h2>{step.title}</h2>
      
      {step.parts && step.parts.map((part, index) => (
        <div key={index} className="concept-part">
          <div className="part-number">{index + 1}</div>
          <div className="part-content">
            <h4>{part.name}</h4>
            <p>{part.explanation}</p>
            {part.example && (
              <code className="part-example">{part.example}</code>
            )}
          </div>
        </div>
      ))}

      {step.diagram && (
        <div className="concept-diagram">
          <img src={`/assets/media/${step.diagram.src}`} alt={step.diagram.alt} />
        </div>
      )}
    </div>
  );

  // New: Render concept type (keyPoints based)
  const renderConcept = () => (
    <div className="step-concept">
      <h2>{step.title}</h2>
      
      {step.content?.text && (
        <p className="concept-intro">{step.content.text}</p>
      )}

      {step.content?.keyPoints && (
        <div className="key-points-list">
          <h4>Key Points:</h4>
          <ul>
            {step.content.keyPoints.map((point, index) => (
              <li key={index} className="key-point-item">
                <span className="key-point-bullet">✓</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step.visual && (
        <div className="concept-visual">
          <img 
            src={`/assets/media/${step.visual.src}`} 
            alt={step.visual.alt || step.title} 
          />
        </div>
      )}
    </div>
  );

  // New: Render explanation type (parts with code/explanation pairs)
  const renderExplanation = () => (
    <div className="step-explanation">
      <h2>{step.title}</h2>
      
      {step.content?.text && (
        <p className="explanation-intro">{step.content.text}</p>
      )}

      {step.content?.parts && (
        <div className="explanation-parts">
          {step.content.parts.map((part, index) => (
            <div key={index} className="explanation-part">
              <code className="part-code">{part.code}</code>
              <span className="part-arrow">→</span>
              <span className="part-desc">{part.explanation}</span>
            </div>
          ))}
        </div>
      )}

      {step.content?.codeExample && (
        <div className="explanation-code-example">
          <CodeEditor
            code={step.content.codeExample}
            language={language}
            editable={false}
            runnable={true}
            onRun={handleRunCode}
            running={isRunning}
          />
          {executionResult && (
            <OutputPanel
              output={executionResult.output}
              error={executionResult.error}
              success={executionResult.success}
              executionTime={executionResult.executionTime}
              visible={true}
            />
          )}
        </div>
      )}
    </div>
  );

  const renderPractice = () => {
    // Support both step.instructions and step.content.instruction
    const instructions = step.instructions || step.content?.instruction || '';
    const starterCode = step.starterCode || step.content?.starterCode || '';
    const hints = step.hints || step.content?.hints || [];

    return (
      <div className="step-practice">
        <h2>{step.title}</h2>
        <p className="practice-instructions">{instructions}</p>
        
        <CodeEditor
          code={starterCode}
          language={language}
          editable={true}
          runnable={true}
          onRun={handleRunCode}
          running={isRunning}
          placeholder="# Write your code here..."
        />
        
        {executionResult && (
          <OutputPanel
            output={executionResult.output}
            error={executionResult.error}
            success={executionResult.success}
            executionTime={executionResult.executionTime}
            visible={true}
          />
        )}

        {hints && hints.length > 0 && (
          <div className="practice-hints">
            <button 
              className="hint-toggle-btn"
              onClick={() => {
                soundService.playClick();
                hapticService.light();
                setHintLevel(prev => prev < hints.length ? prev + 1 : 0);
              }}
            >
              💡 {hintLevel === 0 ? 'Need a hint?' : `Hint ${hintLevel}/${hints.length}`}
            </button>
            
            {hintLevel > 0 && (
              <div className="hints-revealed">
                {hints.slice(0, hintLevel).map((hint, i) => (
                  <div key={i} className="hint-item">
                    <span className="hint-number">Hint {i + 1}:</span>
                    <span>{hint}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Fixed: Quiz now accesses step.quiz.* properties correctly
  const renderQuiz = () => {
    const quizData = step.quiz || step;
    
    return (
      <div className="step-quiz">
        <h2>{step.title || 'Quick Check!'}</h2>
        
        <QuizRenderer
          question={quizData.question}
          options={quizData.options}
          correctAnswer={quizData.correctAnswer}
          explanation={quizData.explanation}
          onAnswer={onAnswer}
          onNext={onComplete}
        />
      </div>
    );
  };

  const renderMiniChallenge = () => (
    <div className="step-mini-challenge">
      <div className="challenge-header">
        <span className="challenge-icon">🎯</span>
        <h2>{step.title}</h2>
      </div>
      
      <p className="challenge-description">{step.description}</p>
      
      <CodeEditor
        code={step.starterCode || ''}
        language={language}
        editable={true}
        runnable={true}
        onRun={handleRunCode}
        running={isRunning}
      />
      
      {executionResult && (
        <OutputPanel
          output={executionResult.output}
          error={executionResult.error}
          success={executionResult.success}
          executionTime={executionResult.executionTime}
          visible={true}
        />
      )}

      {step.expectedOutput && (
        <div className="expected-output">
          <h4>Expected Output:</h4>
          <pre>{step.expectedOutput}</pre>
        </div>
      )}
    </div>
  );

  // New: Render challenge type (with content.task, content.starterCode, etc.)
  const renderChallenge = () => {
    const task = step.content?.task || step.description || '';
    const starterCode = step.content?.starterCode || step.starterCode || '';
    const solution = step.content?.solution || step.solution || '';
    const hints = step.content?.hints || step.hints || [];
    const points = step.reward?.points || 50;

    return (
      <div className="step-challenge">
        <div className="challenge-header">
          <span className="challenge-icon">🏆</span>
          <h2>{step.title}</h2>
        </div>
        
        {/* Challenge Completed Success Message */}
        {challengeCompleted && (
          <div className="challenge-success">
            <div className="success-animation">🎉</div>
            <h3>Challenge Complete!</h3>
            <p>Great job! You earned <strong>+{points} points</strong></p>
            {step.reward?.badge && (
              <div className="badge-earned">
                <span>🏅</span> Badge Unlocked!
              </div>
            )}
          </div>
        )}
        
        {!challengeCompleted && (
          <>
            <div className="challenge-task">
              <p>{task}</p>
            </div>
            
            <CodeEditor
              code={starterCode}
              language={language}
              editable={true}
              runnable={true}
              onRun={handleRunCode}
              running={isRunning}
            />
            
            {executionResult && (
              <OutputPanel
                output={executionResult.output}
                error={executionResult.error}
                success={executionResult.success}
                executionTime={executionResult.executionTime}
                visible={true}
              />
            )}

            {/* Show "Mark Complete" after successful code run */}
            {hasRunCode && executionResult?.success && !executionResult?.error && (
              <div className="challenge-complete-prompt">
                <p>Did your code work correctly?</p>
                <button 
                  className="complete-btn"
                  onClick={handleChallengeComplete}
                >
                  ✅ I Completed This Challenge!
                </button>
              </div>
            )}

            <div className="challenge-actions">
              {hints && hints.length > 0 && (
                <button 
                  className="hint-btn"
                  onClick={() => {
                    soundService.playClick();
                    hapticService.light();
                    setHintLevel(prev => prev < hints.length ? prev + 1 : 0);
                  }}
                >
                  💡 {hintLevel === 0 ? 'Get Hint' : `Hint ${hintLevel}/${hints.length}`}
                </button>
              )}
              
              {solution && (
                <button 
                  className="solution-btn"
                  onClick={() => {
                    soundService.playClick();
                    hapticService.light();
                    setShowSolution(!showSolution);
                  }}
                >
                  {showSolution ? '🙈 Hide Solution' : '👀 Show Solution'}
                </button>
              )}
            </div>

            {hintLevel > 0 && hints.length > 0 && (
              <div className="challenge-hints">
                {hints.slice(0, hintLevel).map((hint, i) => (
                  <div key={i} className="hint-item">
                    <span className="hint-badge">Hint {i + 1}</span>
                    <span>{hint}</span>
                  </div>
                ))}
              </div>
            )}

            {showSolution && solution && (
              <div className="challenge-solution">
                <h4>Solution:</h4>
                <CodeEditor
                  code={solution}
                  language={language}
                  editable={false}
                  runnable={true}
                  onRun={handleRunCode}
                  running={isRunning}
                />
              </div>
            )}

            {step.reward && (
              <div className="challenge-reward">
                <span className="reward-icon">🎖️</span>
                <span>+{points} points</span>
                {step.reward.badge && <span className="badge-unlock">🏅 Badge Unlock!</span>}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSummary = () => {
    // Support both step.keyPoints and step.content.learned
    const keyPoints = step.keyPoints || step.content?.learned || [];
    const nextPreview = step.nextLesson?.title || step.content?.nextPreview || '';

    return (
      <div className="step-summary">
        <div className="summary-header">
          <span className="summary-icon">🎉</span>
          <h2>{step.title || 'Lesson Complete!'}</h2>
        </div>
        
        {step.mascot && (
          <MascotMessage 
            message={step.mascot.message} 
            state={step.mascot.state || 'celebrate'}
          />
        )}
        
        {keyPoints.length > 0 && (
          <div className="key-points">
            <h3>What You Learned:</h3>
            <ul>
              {keyPoints.map((point, i) => (
                <li key={i}>
                  <span className="check-mark">✅</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {nextPreview && (
          <div className="next-lesson-preview">
            <h4>🚀 Next Up:</h4>
            <p>{nextPreview}</p>
          </div>
        )}
      </div>
    );
  };

  // Fixed: Generic now safely handles object content
  const renderGeneric = () => {
    // Extract text safely from various content structures
    let displayText = '';
    if (typeof step.content === 'string') {
      displayText = step.content;
    } else if (step.content?.text) {
      displayText = step.content.text;
    } else if (step.content?.instruction) {
      displayText = step.content.instruction;
    }

    return (
      <div className="step-generic">
        <h2>{step.title}</h2>
        {displayText && <p>{displayText}</p>}
        
        {step.visual && (
          <div className="generic-visual">
            <img 
              src={`/assets/media/${step.visual.src}`} 
              alt={step.visual.alt || step.title}
            />
          </div>
        )}
        
        {step.mascot && (
          <MascotMessage 
            message={step.mascot.message} 
            state={step.mascot.state || 'explain'}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`step-renderer step-type-${step.type}`}>
      {renderStepContent()}
    </div>
  );
}

export default StepRenderer;